import {
  ClockCircleOutlined,
  CloseCircleOutlined,
  FormOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import {CheckCircleFilled} from '@ant-design/icons/lib';
import {Typography} from 'antd';
import {MasterAccountEvent} from 'api/generated';
import dot from 'dot-object';
import {colors} from 'lib/colors';
import {formatIBAN} from 'lib/formaters';
import {isDefined} from 'lib/utils';
import {EventType, MasterAccountKeysUpdate} from 'types';

export type MasterAccountEventWithChanged = MasterAccountEvent & {changed?: string[]};

const {Text} = Typography;

const checkGreen = {
  icon: CheckCircleFilled,
  color: colors.green
};
const clockSecondary = {
  icon: ClockCircleOutlined,
  color: colors.secondary
};
const infoSecondary = {
  icon: InfoCircleOutlined,
  color: colors.secondary
};
const formSecondary = {
  icon: FormOutlined,
  color: colors.secondary
};
const closeRed = {
  icon: CloseCircleOutlined,
  color: colors.red
};

export const eventMapping: Record<
  string,
  {title: string; icon?: any; color?: string; theme?: 'filled'}
> = {
  [EventType.MASTER_ACCOUNT_PENDING]: {
    title: 'Onboarding started',
    ...clockSecondary
  },
  [EventType.MASTER_ACCOUNT_DATA_REQUESTED]: {
    title: 'Additional data requested',
    ...clockSecondary
  },
  [EventType.MASTER_ACCOUNT_APPROVAL_REQUESTED]: {
    title: 'Approval requested',
    ...clockSecondary
  },
  [EventType.MASTER_ACCOUNT_REVIEW_REQUESTED]: {
    title: 'Review requested',
    ...clockSecondary
  },
  [EventType.MASTER_ACCOUNT_APPROVED]: {
    title: 'Account approved',
    ...checkGreen
  },
  [EventType.MASTER_ACCOUNT_ACTIVATED]: {
    title: 'Account activated',
    ...checkGreen
  },
  [EventType.MASTER_ACCOUNT_IBAN_UPDATED]: {
    title: 'IBAN updated',
    ...formSecondary
  },
  [EventType.MASTER_ACCOUNT_REJECTED]: {
    title: 'Account rejected',
    ...closeRed
  },
  [EventType.MASTER_ACCOUNT_DELETED]: {
    title: 'Account deleted',
    ...closeRed
  },
  [EventType.MASTER_ACCOUNT_SUSPENDED]: {
    title: 'Account suspended',
    ...closeRed
  },
  [EventType.MASTER_ACCOUNT_BLOCKED]: {
    title: 'Account blocked',
    ...closeRed
  },
  [EventType.MASTER_ACCOUNT_UNBLOCKED]: {
    title: 'Account unblocked',
    ...checkGreen
  }
};

const descriptionComponentMapping: Record<
  string,
  (value: string, event: MasterAccountEventWithChanged) => JSX.Element | string | null
> = {
  [MasterAccountKeysUpdate.IBAN]: (value) => <div>IBAN: {formatIBAN(value, true)}</div>,
  [MasterAccountKeysUpdate.BLOCKED]: (value) => (
    <Text>{value ? 'Account blocked' : 'Account unblocked'}</Text>
  )
};

export const getEventItem = (event: MasterAccountEventWithChanged) => {
  return (
    eventMapping[event.type] || {
      title: 'Unknown event',
      ...infoSecondary
    }
  );
};

export const sortChangedKeys = (a: string, b: string) => {
  if (a.toLowerCase().includes('note')) return 1;
  if (b.toLowerCase().includes('note')) return -1;
  return 0;
};

export const getMasterAccountEventDescription = (event: MasterAccountEventWithChanged) => {
  const description = event.changed
    ?.map((change) => {
      const value = dot.pick(change, event.object);
      if (!isDefined(value)) return undefined;
      if (Array.isArray(value)) {
        return value[0] ? descriptionComponentMapping[change]?.(value[0], event) : undefined;
      } else {
        return descriptionComponentMapping[change]?.(value, event);
      }
    })
    .filter((value) => isDefined(value));
  return description?.length
    ? description.slice(0, 3).map((item, i) => <div key={i}>{item}</div>)
    : undefined;
};
